import React from 'react';

// Support both snake_case (API) and camelCase formats
interface DepartmentCompletion {
  departmentName?: string;
  departmentId?: string;
  totalEmployees?: number;
  completedCount?: number;
  completionPercentage?: number;
  cycleName?: string;
  // snake_case (from API)
  department_name?: string;
  department_id?: string;
  total_employees?: number;
  completed_count?: number;
  completion_percentage?: number;
  cycle_name?: string;
}

// Helpers for snake_case/camelCase compatibility
const getDepartmentName = (d: DepartmentCompletion) =>
  d.departmentName || d.department_name || 'Unknown';
const getTotalEmployees = (d: DepartmentCompletion) =>
  Number(d.totalEmployees ?? d.total_employees ?? 0);
const getCompletedCount = (d: DepartmentCompletion) =>
  Number(d.completedCount ?? d.completed_count ?? 0);
const getCompletionPercentage = (d: DepartmentCompletion) =>
  Number(d.completionPercentage ?? d.completion_percentage ?? 0);
const getCycleName = (d: DepartmentCompletion) =>
  d.cycleName || d.cycle_name || '';

interface CycleOption {
  value: string;
  label: string;
}

interface ReviewCompletionByDeptWidgetProps {
  data?: DepartmentCompletion[] | { data: DepartmentCompletion[] };
  loading?: boolean;
  error?: string;
  onDepartmentClick?: (departmentId: string, departmentName: string) => void;
  cycleOptions?: CycleOption[];
  selectedCycleId?: string;
  onCycleChange?: (cycleId: string) => void;
  cycleOptionsLoading?: boolean;
}

export const ReviewCompletionByDeptWidget: React.FC<ReviewCompletionByDeptWidgetProps> = ({
  data,
  loading,
  error,
  onDepartmentClick,
  cycleOptions,
  selectedCycleId,
  onCycleChange,
  cycleOptionsLoading,
}) => {
  // Handle wrapped data format
  const departments: DepartmentCompletion[] = Array.isArray(data)
    ? data
    : ((data as { data: DepartmentCompletion[] })?.data || []);

  // Get cycle name from first department
  const cycleName = departments.length > 0 ? getCycleName(departments[0]) : '';

  // Get bar color based on completion percentage
  const getBarColor = (pct: number): string => {
    if (pct >= 70) return 'bg-emerald-500';
    if (pct >= 50) return 'bg-amber-400';
    return 'bg-red-400';
  };

  // Get text color based on completion percentage
  const getTextColor = (pct: number): string => {
    if (pct >= 70) return 'text-emerald-600';
    if (pct >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const renderHeader = () => (
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-semibold text-gray-900 text-sm">Review Completion by Dept</h3>
      {cycleOptions && cycleOptions.length > 0 && onCycleChange && (
        <select
          value={selectedCycleId || ''}
          onChange={(e) => onCycleChange(e.target.value)}
          disabled={cycleOptionsLoading}
          className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 w-[200px] truncate"
        >
          {cycleOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center text-red-500 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (departments.length === 0) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          No review data available
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 h-full flex flex-col">
      {/* Header */}
      {renderHeader()}

      {/* Department List */}
      <div className="flex-1 space-y-3 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
        {departments.map((dept, index) => {
          const name = getDepartmentName(dept);
          const percentage = getCompletionPercentage(dept);
          const completed = getCompletedCount(dept);
          const total = getTotalEmployees(dept);
          const barColor = getBarColor(percentage);
          const textColor = getTextColor(percentage);

          return (
            <div
              key={dept.department_id || dept.departmentId || index}
              className="cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() =>
                onDepartmentClick?.(
                  dept.department_id || dept.departmentId || '',
                  name
                )
              }
            >
              {/* Department name and percentage */}
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-900 truncate" title={name}>
                  {name}
                </span>
                <span className={`text-sm font-semibold ${textColor} ml-2 shrink-0`}>
                  {percentage}%
                </span>
              </div>

              {/* Progress bar */}
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${barColor} rounded-full transition-all duration-500`}
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                />
              </div>

              {/* Employee count */}
              <div className="text-[11px] text-gray-400 mt-1">
                {completed} of {total} completed
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReviewCompletionByDeptWidget;
